import type { AgeBand, DayState, TransformedShelf } from '../types'

const DAY_MS = 24 * 60 * 60 * 1000

function dayStateOf(shelf: TransformedShelf): DayState {
  if (!shelf.source) return 'noData'
  return shelf.source.current ? 'current' : 'notCurrent'
}

/**
 * How many store days before the selected day a shelf was observed. Counts back from the end of the
 * selected store day, which the backend sends, so the frontend doesn't need to know when a day starts.
 * shortcut: assumes 24-hour days, so it can be off by one for a scan in the first hour of the day
 * that winter time ends; only used for display, fine unless the source line needs to be exact there.
 */
function daysBeforeDayOf(observedAt: string, dayAsOf: string) {
  const age = new Date(dayAsOf).getTime() - new Date(observedAt).getTime()
  // asOf is exclusive: the first moment that no longer belongs to the selected day
  return Math.max(0, Math.floor((age - 1) / DAY_MS))
}

/** The data-age layer's bands, in store days before the selected day. */
export function ageBandOf(daysBeforeDay: number): AgeBand {
  if (daysBeforeDay === 0) return 'sameDay'
  if (daysBeforeDay <= 3) return 'upTo3Days'
  if (daysBeforeDay <= 7) return 'upTo7Days'
  return 'older'
}

/**
 * What a shelf shows in the day view. The backend decides, this only names it:
 * current: its observation is recent enough to show stock and prices.
 * notCurrent: an older observation; products and position, no stock and no prices.
 * noData: on the map, but no observation in the window.
 *
 * @param dayAsOf the end of the selected day; a layout of a single scan has none
 */
export function applyDayStateTransformation(shelf: TransformedShelf, dayAsOf: string | null | undefined): TransformedShelf {
  if (!dayAsOf) return shelf

  return {
    ...shelf,
    dayState: dayStateOf(shelf),
    daysBeforeDay: shelf.source ? daysBeforeDayOf(shelf.source.observedAt, dayAsOf) : undefined,
  }
}
